// @flow
/* global SyntheticClipboardEvent */

import * as React from 'react';
import { cx, css } from 'emotion';
import PropTypes from 'prop-types';
import R from 'ramda';
import ReactSelect, { Creatable } from 'react-select';

import Title from './Title';
import wrappedComponents from './dropdown/components';
import { getClearable } from './dropdown/compat';
import { customStyles } from './dropdown/styles';
import MenuList from './dropdown/MenuList';
import type { TitleProps } from '../../types/titleTypes';

const REQUIRED_FOOTNOTE = '*This field is required';

/**
 * For all property options, see: https://github.com/JedWatson/react-select
 *
 * Props that we want to handle instead of directly passing into react-select:
 */
export const HANDLED_PROPS = [
  'components',
  'disabled',
  'testid',
  'onChange',
  'onOpen',
  'i13nLabel',
  'outerClassName',
  'label',
  'labelClassName',
  'requiredNote',
  'childRef',
  'backspaceRemovesValue',
  'styles'
];

export type SelectEvent = {
  value: string,
  label: string
};

export type SelectEventAction = {
  action: string
};

export type DropdownProps = {
  styles?: any,

  components?: any,

  /**
   * Handled separately for the purposes of mixpanel tracking
   *
   * For regular dropdowns, e will be SelectEvent.
   * If dropdown is a multiselect, e will be an array of SelectEvents.
   */
  onChange: (e: any, action: SelectEventAction) => void,
  /**
   * Required for QA testing, applied to outer div
   */
  testid: string,
  /**
   * Allow creation of new options by typing in select box
   */
  creatable?: boolean,
  /**
   * Label prefix for mixpanel tracking. If no 'label' props is passed in,
   * be sure to include an i13nLabel/Model so there is an event name to fire!
   */
  i13nLabel?: string,
  /**
   * Model passed into the mixpanel event. (Not necessary for most use cases).
   * label attribute can be overridden by i13nLabel.
   */
  i13nModel?: Object,
  /**
   * Optional callback invoked when select component is opened
   */
  onOpen?: () => void,
  /**
   * className applied to outer div
   */
  outerClassName?: string,
  /**
   * Show "*This is required" below the dropdown if true
   */
  requiredNote?: boolean,

  shouldKeyDownEventCreateNewOption?: Object => boolean,
  inputProps?: {
    onPaste: (SyntheticClipboardEvent<any>) => void
  },
  childRef?: ?{ pasteInput: boolean },
  ref?: () => void,
  menuRenderer?: () => void,
  noResultsText?: ?string,
  multi?: boolean,
  clearable?: boolean,
  className?: string,
  placeholder?: string | React.Node,
  value: any,
  disabled?: boolean,
  components?: any,
  optionRenderer?: (option: any) => any,
  options?: any[],
  backspaceRemoves?: boolean,
  searchable?: boolean,
  closeMenuOnSelect?: boolean
} & TitleProps;

export const getValue = (options: any[] = [], value?: any) => {
  if (value === undefined) {
    return value;
  }

  if (value === null) {
    return null;
  }

  if (Array.isArray(value)) {
    return value.map(single => getValue(options, single)).filter(Boolean);
  }

  if (value instanceof Object) {
    return value;
  }

  return findValueInOptions(value, options);
};

export function findValueInOptions(value?: any, options: any[]) {
  const foundValues = options.filter(Boolean).map(option => {
    if (option.options) {
      return findValueInOptions(value, option.options);
    }
    return option.value === value ? option : undefined;
  });
  return foundValues.find(Boolean);
}

class Dropdown extends React.PureComponent<DropdownProps> {
  constructor(props: DropdownProps) {
    super(props);
    if (!props.i13nLabel && !props.i13nModel) {
      console.warn(
        'One of i13nLabel or i13nModel must be passed to the Dropdown component.'
      );
    }
  }

  /*
   * For regular dropdowns, e will be type: SelectEvent obj.
   * If dropdown is a multiselect, e will be type: an array of SelectEvent objs.
   */
  fireMixpanelEvent = (type: 'open' | 'change', e?: any) => {
    if (!this.context.i13n) {
      return;
    }
    // Set defaults
    let i13nModel = this.props.i13nModel || {};
    const i13nLabel = i13nModel.label || this.props.i13nLabel || '';

    // Add "- Dropdown open" or "- Dropdown change" to the label
    const label = `${i13nLabel} - Dropdown ${type}`;
    // Merge params
    const params = R.merge(
      { component: 'Select', option: e || {} },
      i13nModel.params
    );
    // Final merge incase there are other properties passed besides label and params
    i13nModel = R.merge(i13nModel, { label, params });
    this.context.i13n.executeEvent('log', i13nModel);
  };

  _onOpen = () => {
    this.props.onOpen && this.props.onOpen();
    this.fireMixpanelEvent('open');
  };

  /*
   * For regular dropdowns, e will be type: SelectEvent obj.
   * If dropdown is a multiselect, e will be type: an array of SelectEvent objs.
   */
  _onChange = (
    e: SelectEvent | Array<SelectEvent>,
    action: SelectEventAction
  ) => {
    this.props.onChange(e, action);
    this.fireMixpanelEvent('change', e);
  };

  render() {
    const props = this.props;
    const Component = props.creatable ? Creatable : ReactSelect;
    const childProps = R.omit(HANDLED_PROPS, { ...props });
    if (props.creatable && props.childRef) {
      const { childRef = {} } = props;
      // paste requires a reference to react-select component internals
      childProps.ref = ref => (childRef.pasteInput = ref);
    }

    const getOption = (): any => {
      const optionRenderer: any = props.optionRenderer;
      if (optionRenderer) {
        return (optionProps: any) => {
          const { cx, getStyles } = optionProps;
          const className = cx(css(getStyles('option', optionProps)));
          return (
            <div
              ref={optionProps.innerRef}
              className={className}
              {...optionProps.innerProps}
            >
              {optionRenderer(optionProps.data)}
            </div>
          );
        };
      } else if (props.components && props.components.Option) {
        return props.components.Option;
      } else {
        return (optionProps: any) => {
          return <wrappedComponents.Option {...optionProps} />;
        };
      }
    };

    const className = cx(
      props.className,
      'Select',
      props.multi ? 'Select--multi' : 'Select--single',
      props.disabled ? 'is-disabled' : undefined
    );

    return (
      <div className={cx(props.outerClassName)} data-testid={props.testid}>
        <Title
          label={props.label}
          description={props.description}
          hoverableTooltip={props.hoverableTooltip}
          tooltip={props.tooltip}
          labelClassName={props.labelClassName}
        />
        <Component
          {...childProps}
          createOptionPosition={props.creatable && 'first'}
          isSearchable={props.searchable}
          styles={{ ...customStyles, ...(props.styles || {}) }}
          className={className}
          classNamePrefix="Select"
          backspaceRemovesValue={props.backspaceRemoves}
          isDisabled={props.disabled}
          value={getValue(props.options, props.value)}
          components={{
            ...wrappedComponents,
            ...(this.props.components || {}),
            Option: getOption(),
            MenuList
          }}
          isClearable={getClearable(props)}
          isMulti={props.multi}
          isOptionDisabled={option => option.disabled}
          closeMenuOnSelect={props.closeMenuOnSelect || !props.multi} // force dropdown to remain open for multiselect dropdowns
          onChange={this._onChange}
          onMenuOpen={this._onOpen}
        />
        {props.requiredNote && (
          <span className="input-control__note h_ta-start">
            {REQUIRED_FOOTNOTE}
          </span>
        )}
      </div>
    );
  }
}

Dropdown.contextTypes = {
  i13n: PropTypes.object
};

export default Dropdown;
